import { ClaimActionButton } from 'components/ClaimRewardsButton/ClaimActionButton'
import { useRewards } from 'pages/Islands/IslandPage/context/RewardContext'
import { useEffect, useMemo, useState } from 'react'
import { useIsTransactionConfirmed, useIsTransactionPending } from 'state/transactions/hooks'

interface FarmClaimButtonProps {
  fullWidth?: boolean
  className?: string
  modalType?: FarmClaimModalType
  providerKey?: 'kodiak' | 'bgt'
  onEarningsStateChange?: (providerKey: 'kodiak' | 'bgt', hasCurrentEarnings: boolean) => void
  onClaimSuccess?: (providerKey: 'kodiak' | 'bgt') => void
}

export type FarmClaimModalType = 'farmClaim' | 'vaultClaim'

export function FarmClaimButton({
  fullWidth,
  className,
  modalType = 'farmClaim',
  providerKey,
  onEarningsStateChange,
  onClaimSuccess,
}: FarmClaimButtonProps) {
  const {
    openModal,
    claimTxState,
    anyTransactionInProgress,
    refreshRewardsData,
    hasKodiakFarmEarnings,
    hasRewardVaultEarnings,
    kodiakFarmEarned,
    rewardVaultEarned,
  } = useRewards()
  const [lastClaimHash, setLastClaimHash] = useState<string | null>(null)
  const [reportedClaimHash, setReportedClaimHash] = useState<string | null>(null)

  const resolvedProviderKey: 'kodiak' | 'bgt' = providerKey || (modalType === 'vaultClaim' ? 'bgt' : 'kodiak')

  const hasCurrentEarnings = useMemo(() => {
    if (modalType === 'vaultClaim') {
      if (hasRewardVaultEarnings) return true
      return rewardVaultEarned.some((item) => item.amount?.greaterThan('0'))
    }

    if (hasKodiakFarmEarnings) return true
    return kodiakFarmEarned.some((item) => item.amount?.greaterThan('0'))
  }, [modalType, hasKodiakFarmEarnings, hasRewardVaultEarnings, kodiakFarmEarned, rewardVaultEarned])

  useEffect(() => {
    if (claimTxState.hash) {
      setLastClaimHash(claimTxState.hash)
    }
  }, [claimTxState.hash])

  const isPending = useIsTransactionPending(lastClaimHash || undefined)
  const isConfirmed = useIsTransactionConfirmed(lastClaimHash || undefined)
  const isClaiming = claimTxState.attemptingTx || isPending
  const isClaimed = !!lastClaimHash && isConfirmed && !claimTxState.error

  useEffect(() => {
    if (!lastClaimHash || !isClaimed) return
    if (reportedClaimHash === lastClaimHash) return

    setReportedClaimHash(lastClaimHash)
    void refreshRewardsData()
    onClaimSuccess?.(resolvedProviderKey)
  }, [isClaimed, lastClaimHash, onClaimSuccess, refreshRewardsData, reportedClaimHash, resolvedProviderKey])

  useEffect(() => {
    if (onEarningsStateChange) {
      onEarningsStateChange(resolvedProviderKey, hasCurrentEarnings)
    }
  }, [resolvedProviderKey, hasCurrentEarnings, onEarningsStateChange])

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    openModal(modalType)
  }

  if (!hasCurrentEarnings) {
    return null
  }

  return (
    <ClaimActionButton
      className={className}
      disabled={isClaiming || anyTransactionInProgress}
      fullWidth={fullWidth}
      isClaiming={isClaiming}
      isClaimed={isClaimed}
      onClick={handleClick}
    />
  )
}
